import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ImageViewer from 'react-simple-image-viewer';
import { Autocomplete, Box, Button, Card, Chip, Container, Grid, IconButton, Stack, SwipeableDrawer, TextField, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { PictureAsPdf } from '@material-ui/icons';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { BASE_URL, COMPANY_DATA_FOR_PDF } from '../../../../config';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import Iconify from '../../../../components/Iconify';
import PdfViewerDrawer from '../../../../components/pdfViewerDrawer';
import PermissionRequired from '../../../errorPages/permissionRequired';
import { accessVerify, handleNumber, safeJSONParse, utcMoment } from '../../../../utils/common';
import { fCurrency } from '../../../../utils/formatNumber';
import { GLOBAL_LOCATION_ID } from '../../../../utils/constants';
import { imageUpload } from '../../../../inteceptor';
import CashFlowImageUploader, { isImageDocument } from '../../finance/cashflow/components/cashflowImageUploader';
import { getUtilitySuppliers } from '../../../../redux/slices/utilityRedux/utilitySuppliersRedux';
import { addUtilityBill, getUtilityBills, updateUtilityBill } from '../../../../redux/slices/utilityRedux/utilityBillsRedux';

const BILL_TYPES = [
  { label: 'Regular Bill', value: 'REGULAR' },
  { label: 'Service Bill', value: 'SERVICE' },
];

const STATUS_META = {
  PENDING_FINANCE_CONFIRMATION: { label: 'Pending Finance Confirm', color: 'info' },
  REJECTED: { label: 'Rejected', color: 'warning' },
  DELETED: { label: 'Deleted', color: 'default' },
  FINANCE_CONFIRMED: { label: 'Finance Confirmed', color: 'success' },
  PARTIALLY_PAID: { label: 'Partially Paid', color: 'secondary' },
  PAID: { label: 'Paid', color: 'success' },
};

const createDefaultDataModel = (selectedLocation) => ({
  id: '',
  locationId: selectedLocation?.id && selectedLocation?.id !== GLOBAL_LOCATION_ID ? selectedLocation.id : null,
  utilitySupplierId: null,
  billType: 'REGULAR',
  referenceNumber: '',
  billDate: utcMoment().format('YYYY-MM-DD'),
  billingPeriodStart: '',
  billingPeriodEnd: '',
  dueDate: '',
  previousReading: '',
  currentReading: '',
  serviceDescription: '',
  totalAmount: '',
  linkedDocuments: [],
  status: 'PENDING_FINANCE_CONFIRMATION',
});

const normalizeDocuments = (linkedDocuments) => {
  const parsedDocuments = safeJSONParse(linkedDocuments);
  if (!Array.isArray(parsedDocuments)) return [];
  return parsedDocuments
    .map((value) => {
      if (typeof value === 'string') return value;
      return value?.url;
    })
    .filter((value) => value);
};

const getDateForInput = (value) => (value ? utcMoment(value).format('YYYY-MM-DD') : '');

const getSupplierLabel = (option) =>
  option?.name
    ? `${option.name}${option?.code ? ` (${option.code})` : ''}${option?.billingAccountName || option?.utilityAccountName ? ` - ${option.billingAccountName || option.utilityAccountName}` : ''}${
        option?.meterNumber ? ` / ${option.meterNumber}` : ''
      }`
    : '';

export default function UtilityBills() {
  const { themeStretch } = useSettings();
  const { selectedLocation, data: allLocations } = useSelector((state) => state.location);
  const { loggedInUser } = useSelector((state) => state.user);
  const { data: utilitySuppliers } = useSelector((state) => state.utilitySuppliers);
  const { data: bills, addData } = useSelector((state) => state.utilityBills);

  const [selectedDataObj, setSelectedDataObj] = useState(createDefaultDataModel(selectedLocation));
  const [isAdd, setIsAdd] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState(false);
  const [files, setFiles] = useState([]);
  const [documentGallery, setDocumentGallery] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [pdfRow, setPdfRow] = useState(null);
  const [workflowLog, setWorkflowLog] = useState([]);
  const imageGallery = documentGallery?.filter(isImageDocument) || [];

  const activeSuppliers = useMemo(() => (utilitySuppliers || []).filter((value) => Number(value?.status) === 1), [utilitySuppliers]);
  const businessLocations = useMemo(() => (allLocations || []).filter((value) => Number(value?.id) !== GLOBAL_LOCATION_ID), [allLocations]);
  const isGlobalLocation = Number(selectedLocation?.id) === GLOBAL_LOCATION_ID;
  const filteredSuppliers = useMemo(
    () => activeSuppliers.filter((value) => !selectedDataObj?.locationId || Number(value?.locationId) === Number(selectedDataObj?.locationId)),
    [activeSuppliers, selectedDataObj?.locationId]
  );
  const selectedSupplier = useMemo(
    () => (utilitySuppliers || []).find((value) => Number(value?.id) === Number(selectedDataObj?.utilitySupplierId)),
    [utilitySuppliers, selectedDataObj?.utilitySupplierId]
  );
  const canEditSelectedBill = isAdd || selectedDataObj?.status === 'REJECTED';
  const locationActionColor = selectedLocation?.colorCode || undefined;
  const locationActionSx = locationActionColor
    ? {
        bgcolor: locationActionColor,
        '&:hover': {
          bgcolor: locationActionColor,
          opacity: 0.9,
        },
      }
    : {};
  const billBillingType = selectedSupplier?.billingType || selectedDataObj?.accountBillingType || '';
  const isRegularMeteredBill = selectedDataObj?.billType === 'REGULAR' && billBillingType === 'METERED';
  const unitsPerPeriod =
    isRegularMeteredBill && selectedDataObj?.previousReading !== '' && selectedDataObj?.currentReading !== '' ? Number(selectedDataObj.currentReading) - Number(selectedDataObj.previousReading) : '';

  useEffect(() => {
    dispatch(getUtilitySuppliers());
  }, []);

  useEffect(() => {
    if (selectedLocation?.id) {
      dispatch(getUtilityBills(selectedLocation.id));
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    if (addData?.data) {
      dispatch(getUtilityBills(selectedLocation?.id));
      dispatch(getUtilitySuppliers());
      setIsModelOpen(false);
      setSelectedDataObj(createDefaultDataModel(selectedLocation));
      setFiles([]);
      setDocumentGallery([]);
    }
  }, [addData]);

  const manageModel = (modelData = createDefaultDataModel(selectedLocation), type = 'add') => {
    if (isModelOpen) {
      setIsModelOpen(false);
      setSelectedDataObj(createDefaultDataModel(selectedLocation));
      setFiles([]);
      setDocumentGallery([]);
    } else {
      const normalizedDocuments = type === 'add' ? [] : normalizeDocuments(modelData?.linkedDocuments);
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      setFiles([]);
      setDocumentGallery(normalizedDocuments);
      setSelectedDataObj(
        type === 'add'
          ? createDefaultDataModel(selectedLocation)
          : {
              ...modelData,
              billDate: getDateForInput(modelData?.billDate),
              billingPeriodStart: getDateForInput(modelData?.billingPeriodStart),
              billingPeriodEnd: getDateForInput(modelData?.billingPeriodEnd),
              dueDate: getDateForInput(modelData?.dueDate),
              previousReading: modelData?.previousReading || '',
              currentReading: modelData?.currentReading || '',
              totalAmount: modelData?.totalAmount || '',
              linkedDocuments: normalizedDocuments,
            }
      );
    }
  };

  const updateEditingData = (key, value) => {
    setSelectedDataObj((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleLocationChange = (locationId) => {
    setSelectedDataObj((prev) => ({
      ...prev,
      locationId,
      utilitySupplierId: null,
    }));
  };

  const handleSupplierChange = (supplier) => {
    setSelectedDataObj((prev) => ({
      ...prev,
      utilitySupplierId: supplier?.id || null,
    }));
  };

  const uploadFiles = (file) =>
    new Promise((resolve, reject) => {
      imageUpload(
        file,
        `Utilities/Bills/Loc_${selectedDataObj?.locationId || selectedLocation?.id}/User_${loggedInUser?.id}`,
        (data) => {
          if (data?.status) {
            resolve(data);
          } else {
            reject(new Error('Upload failed'));
          }
        },
        (errorMessage) => {
          reject(new Error(errorMessage));
        }
      );
    });

  const handleImageUpload = async () => {
    if (!files.length) {
      toast.warning('Please select at least one document to upload.');
      return;
    }

    setIsUploading(true);
    for (const file of files) {
      try {
        setFiles((value) =>
          value.map((fileValue) =>
            file?.id === fileValue?.id
              ? {
                  ...fileValue,
                  isUploading: true,
                }
              : fileValue
          )
        );

        const data = await uploadFiles(file);
        setFiles((value) => value.filter((fileValue) => file?.id !== fileValue?.id));
        if (data?.status) {
          setDocumentGallery((prevState) => [...prevState, data?.url]);
        }
      } catch (error) {
        setFiles((value) =>
          value.map((fileValue) =>
            file?.id === fileValue?.id
              ? {
                  ...fileValue,
                  isUploading: false,
                }
              : fileValue
          )
        );
      }
    }
    setIsUploading(false);
  };

  const validateBeforeSubmit = () => {
    if (!selectedDataObj.locationId || Number(selectedDataObj.locationId) === GLOBAL_LOCATION_ID) {
      toast.error('Business location is required!');
      return false;
    }
    if (!selectedDataObj.utilitySupplierId) {
      toast.error('Utility supplier account / meter is required!');
      return false;
    }
    if (!selectedDataObj.referenceNumber?.trim()) {
      toast.error('Reference number is required!');
      return false;
    }
    if (!selectedDataObj.billDate) {
      toast.error('Bill date is required!');
      return false;
    }
    if (!Number(selectedDataObj.totalAmount) || Number(selectedDataObj.totalAmount) <= 0) {
      toast.error('Total amount must be greater than zero!');
      return false;
    }
    if (selectedDataObj.billType === 'REGULAR' && billBillingType === 'METERED') {
      if (selectedDataObj.previousReading === '') {
        toast.error('Previous reading is required for regular metered utility bills!');
        return false;
      }
      if (selectedDataObj.currentReading === '') {
        toast.error('Current reading is required for regular metered utility bills!');
        return false;
      }
      if (Number(selectedDataObj.currentReading) < Number(selectedDataObj.previousReading)) {
        toast.error('Current reading cannot be less than previous reading!');
        return false;
      }
    }
    if (selectedDataObj.billType === 'SERVICE' && !selectedDataObj.serviceDescription?.trim()) {
      toast.error('Service description is required for service bills!');
      return false;
    }
    if (!documentGallery.length) {
      toast.error('Please upload at least one bill document!');
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateBeforeSubmit()) return;

    const payload = {
      ...selectedDataObj,
      locationId: Number(selectedDataObj.locationId),
      utilitySupplierId: Number(selectedDataObj.utilitySupplierId),
      billType: selectedDataObj.billType,
      referenceNumber: selectedDataObj.referenceNumber.trim(),
      billDate: selectedDataObj.billDate,
      billingPeriodStart: selectedDataObj.billingPeriodStart || null,
      billingPeriodEnd: selectedDataObj.billingPeriodEnd || null,
      dueDate: selectedDataObj.dueDate || null,
      previousReading: selectedDataObj.previousReading === '' ? null : Number(selectedDataObj.previousReading),
      currentReading: selectedDataObj.currentReading === '' ? null : Number(selectedDataObj.currentReading),
      serviceDescription: selectedDataObj.serviceDescription || '',
      totalAmount: Number(selectedDataObj.totalAmount),
      linkedDocuments: documentGallery,
    };

    dispatch(isAdd ? addUtilityBill(payload) : updateUtilityBill(payload));
  };

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  const normalizePdfDocuments = (linkedDocuments) =>
    normalizeDocuments(linkedDocuments).map((url) => url?.replace('https://storage.googleapis.com/bubblemania/', `${BASE_URL}imageViewer?path=`));

  const getUtilityBillPdfDescription = (bill) => {
    const period =
      bill?.billingPeriodStart || bill?.billingPeriodEnd
        ? `Period: ${getDateForInput(bill?.billingPeriodStart) || '-'} to ${getDateForInput(bill?.billingPeriodEnd) || '-'}`
        : null;
    const readings =
      bill?.billType === 'REGULAR' && bill?.accountBillingType === 'METERED'
        ? `Readings: ${bill?.previousReading ?? '-'} to ${bill?.currentReading ?? '-'}`
        : null;
    const service = bill?.billType === 'SERVICE' ? `Service: ${bill?.serviceDescription || '-'}` : null;

    return [BILL_TYPES.find((value) => value.value === bill?.billType)?.label || bill?.billType, period, readings, service].filter(Boolean).join(' | ');
  };

  const parseWorkflowLogs = (bill) => {
    const parsedLog = safeJSONParse(bill?.workflowLog);
    if (!Array.isArray(parsedLog)) return [];

    return parsedLog.map((log) => ({
      ...log,
      date: log?.date ? utcMoment(log.date).format('YYYY-MM-DD HH:mm:ss') : '',
    }));
  };

  const handleDownload = (row, event) => {
    event?.stopPropagation();
    const bill = row?.original || {};
    const totalAmount = handleNumber(bill?.totalAmount);
    const paidAmount = handleNumber(bill?.paidAmount);

    setPdfRow({
      invoiceNumber: `UB-${bill?.id}`,
      createDate: bill?.billDate ? utcMoment(bill.billDate).format('DD MMMM yyyy').toString() : '-',
      dueDate: bill?.dueDate ? utcMoment(bill.dueDate).format('DD MMMM yyyy').toString() : '-',
      status: STATUS_META[bill?.status]?.label || bill?.status || '-',
      discount: 0,
      taxes: 0,
      paidAmount,
      subTotalPrice: totalAmount,
      totalPrice: totalAmount - paidAmount,
      topic: 'Utility Bill',
      referenceNumber: bill?.referenceNumber,
      invoiceFrom: {
        name: bill?.utilitySupplierName || '-',
        address: bill?.utilitySupplierAddress || '',
        phone: bill?.utilitySupplierContactNumber || '',
      },
      invoiceTo: {
        name: COMPANY_DATA_FOR_PDF?.name,
        address: bill?.locationAddress || '',
        phone: bill?.locationContactNumber || '',
      },
      items: [
        {
          id: 1,
          title: `${bill?.utilityCategoryName || 'Utility'} - ${bill?.billingAccountName || bill?.utilityAccountName || bill?.accountName || '-'}`,
          description: getUtilityBillPdfDescription(bill),
          quantity: 1,
          measurementLabel: 'Billing Type',
          measurementValue: bill?.accountBillingType === 'METERED' ? 'Metered' : bill?.accountBillingType === 'NON_METERED' ? 'Non Metered' : '-',
          price: totalAmount,
        },
      ],
      stockCodes: [],
      linkedDocuments: normalizePdfDocuments(bill?.linkedDocuments),
    });
    setWorkflowLog(parseWorkflowLogs(bill));
    setIsPdfDrawerOpen(true);
  };

  const columns = [
    { accessorKey: 'id', header: 'ID' },
    { accessorKey: 'locationName', header: 'Location' },
    {
      accessorKey: 'billingAccountName',
      header: 'Account / Meter',
      Cell: ({ row }) =>
        `${row?.original?.billingAccountName || row?.original?.utilityAccountName || row?.original?.accountName || '-'}${row?.original?.meterNumber ? ` (${row?.original?.meterNumber})` : ''}`,
    },
    {
      accessorKey: 'billingAccountNumber',
      header: 'Account Number',
      Cell: ({ row }) => row?.original?.billingAccountNumber || row?.original?.utilityAccountNumber || row?.original?.accountNumber || '-',
    },
    { accessorKey: 'accountBillingType', header: 'Billing Type' },
    { accessorKey: 'utilityCategoryName', header: 'Category' },
    {
      accessorKey: 'utilitySupplierName',
      header: 'Supplier',
      Cell: ({ row }) => (row?.original?.utilitySupplierName ? `${row?.original?.utilitySupplierName}${row?.original?.utilitySupplierCode ? ` (${row?.original?.utilitySupplierCode})` : ''}` : '-'),
    },
    { accessorKey: 'referenceNumber', header: 'Reference Number' },
    {
      accessorKey: 'billType',
      header: 'Bill Type',
      Cell: ({ row }) => BILL_TYPES.find((value) => value.value === row?.original?.billType)?.label || row?.original?.billType,
    },
    {
      accessorKey: 'billingPeriodStart',
      header: 'Period',
      Cell: ({ row }) =>
        row?.original?.billingPeriodStart || row?.original?.billingPeriodEnd
          ? `${getDateForInput(row?.original?.billingPeriodStart) || '-'} to ${getDateForInput(row?.original?.billingPeriodEnd) || '-'}`
          : '-',
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      Cell: ({ row }) => getDateForInput(row?.original?.dueDate) || '-',
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      Cell: ({ row }) => fCurrency(row?.original?.totalAmount),
    },
    {
      accessorKey: 'paidAmount',
      header: 'Paid Amount',
      Cell: ({ row }) => fCurrency(row?.original?.paidAmount),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ row }) => {
        const statusMeta = STATUS_META[row?.original?.status] || { label: row?.original?.status || '-', color: 'default' };
        return <Chip size='small' label={statusMeta.label} color={statusMeta.color} sx={{ minWidth: 110 }} />;
      },
    },
    {
      accessorKey: 'submittedByName',
      header: 'Submitted By',
      Cell: ({ row }) => row?.original?.submittedByName || '-',
    },
  ];

  return (
    <Page title='Utility Bills'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Utility Bills'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Administration', href: PATH_DASHBOARD.administration.root }, { name: 'Utilities' }, { name: 'Utility Bills' }]}
          action={
            accessVerify('UTILITY_BILL_ADD') ? (
              <Button variant='contained' startIcon={<Iconify icon='eva:plus-fill' />} sx={locationActionSx} onClick={() => manageModel(createDefaultDataModel(selectedLocation), 'add')}>
                New Utility Bill
              </Button>
            ) : null
          }
        />

        {accessVerify('UTILITY_BILL_VIEW') ? (
          <DataGridTable
            name='Utility Bill List'
            data={bills || []}
            column={columns}
            isLoading={false}
            onRowClick={(row) => {
              manageModel(row?.original, row?.original?.status === 'REJECTED' ? 'edit' : 'view');
            }}
            isRowClickable={true}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowSelection={false}
            enableRowActions={true}
            renderRowActionItems={(row) => [
              <Tooltip title='View PDF' key='pdf'>
                <IconButton color='error' onClick={(event) => handleDownload(row, event)}>
                  <PictureAsPdf />
                </IconButton>
              </Tooltip>,
            ]}
          />
        ) : (
          <PermissionRequired />
        )}

        <SwipeableDrawer
          anchor='top'
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => manageModel()}
          PaperProps={{
            sx: {
              width: '94%',
              height: '92%',
              marginLeft: '3%',
              marginTop: '2%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <h1>{isAdd ? 'Utility Bill' : selectedDataObj?.status === 'REJECTED' ? 'Correct Utility Bill' : 'View Utility Bill'}</h1>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8}>
              <Stack spacing={3} sx={{ p: 3 }}>
                <Card sx={{ p: 3 }}>
                  <Typography variant='subtitle1' padding='10px 0'>
                    Bill Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        onChange={(event, newValue) => handleLocationChange(newValue?.id || null)}
                        options={businessLocations}
                        value={businessLocations?.find((value) => Number(value?.id) === Number(selectedDataObj?.locationId)) || null}
                        renderInput={(params) => <TextField label='Location' placeholder='Select business location' {...params} />}
                        getOptionLabel={(option) => option?.name || ''}
                        isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                        disabled={!isGlobalLocation || !canEditSelectedBill}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        onChange={(event, newValue) => handleSupplierChange(newValue)}
                        options={filteredSuppliers}
                        value={filteredSuppliers?.find((value) => Number(value?.id) === Number(selectedDataObj?.utilitySupplierId)) || null}
                        renderInput={(params) => <TextField label='Utility Supplier Account / Meter' placeholder='Select supplier account / meter' {...params} />}
                        getOptionLabel={getSupplierLabel}
                        isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                        disabled={!canEditSelectedBill}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        onChange={(event, newValue) => updateEditingData('billType', newValue?.value || 'REGULAR')}
                        options={BILL_TYPES}
                        value={BILL_TYPES.find((value) => value.value === selectedDataObj?.billType) || null}
                        renderInput={(params) => <TextField label='Bill Type' {...params} />}
                        getOptionLabel={(option) => option?.label || ''}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        disabled={!canEditSelectedBill}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label='Category' value={selectedSupplier?.utilityCategoryName || selectedDataObj?.utilityCategoryName || ''} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label='Billing Type' value={billBillingType || ''} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Account / Meter Name'
                        value={selectedSupplier?.billingAccountName || selectedSupplier?.utilityAccountName || selectedDataObj?.billingAccountName || selectedDataObj?.utilityAccountName || selectedDataObj?.accountName || ''}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Account Number'
                        value={selectedSupplier?.billingAccountNumber || selectedSupplier?.utilityAccountNumber || selectedDataObj?.billingAccountNumber || selectedDataObj?.utilityAccountNumber || selectedDataObj?.accountNumber || ''}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label='Meter Number' value={selectedSupplier?.meterNumber || selectedDataObj?.meterNumber || ''} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label='Reference Number'
                        placeholder='Example: Supplier bill or invoice reference'
                        value={selectedDataObj?.referenceNumber || ''}
                        onChange={(e) => updateEditingData('referenceNumber', e.target.value)}
                        disabled={!canEditSelectedBill}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Bill Date'
                        type='date'
                        value={selectedDataObj?.billDate || ''}
                        onChange={(e) => updateEditingData('billDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={!canEditSelectedBill}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Billing Period Start'
                        type='date'
                        value={selectedDataObj?.billingPeriodStart || ''}
                        onChange={(e) => updateEditingData('billingPeriodStart', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={!canEditSelectedBill}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Billing Period End'
                        type='date'
                        value={selectedDataObj?.billingPeriodEnd || ''}
                        onChange={(e) => updateEditingData('billingPeriodEnd', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={!canEditSelectedBill}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Due Date'
                        type='date'
                        value={selectedDataObj?.dueDate || ''}
                        onChange={(e) => updateEditingData('dueDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={!canEditSelectedBill}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Total Amount'
                        type='number'
                        placeholder='Example: 25000.00'
                        value={selectedDataObj?.totalAmount || ''}
                        onChange={(e) => updateEditingData('totalAmount', e.target.value)}
                        disabled={!canEditSelectedBill}
                      />
                    </Grid>
                    {isRegularMeteredBill && (
                      <>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label='Previous Reading'
                            type='number'
                            placeholder='Example: 10250'
                            value={selectedDataObj?.previousReading || ''}
                            onChange={(e) => updateEditingData('previousReading', e.target.value)}
                            disabled={!canEditSelectedBill}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label='Current Reading'
                            type='number'
                            placeholder='Example: 10680'
                            value={selectedDataObj?.currentReading || ''}
                            onChange={(e) => updateEditingData('currentReading', e.target.value)}
                            disabled={!canEditSelectedBill}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label='Units Per Period'
                            value={unitsPerPeriod === '' || Number.isNaN(unitsPerPeriod) ? '' : unitsPerPeriod}
                            InputProps={{ readOnly: true }}
                            error={unitsPerPeriod !== '' && !Number.isNaN(unitsPerPeriod) && unitsPerPeriod < 0}
                          />
                        </Grid>
                      </>
                    )}
                    {selectedDataObj?.billType === 'SERVICE' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label='Service Description'
                          placeholder='Example: Utility repair or service charge details'
                          value={selectedDataObj?.serviceDescription || ''}
                          onChange={(e) => updateEditingData('serviceDescription', e.target.value)}
                          disabled={!canEditSelectedBill}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Card>

                <CashFlowImageUploader
                  files={canEditSelectedBill ? files : []}
                  setFiles={setFiles}
                  handleImageUpload={handleImageUpload}
                  documentGallery={documentGallery}
                  openImageViewer={openImageViewer}
                  isUploading={isUploading || !canEditSelectedBill}
                />
                {canEditSelectedBill && documentGallery.length > 0 && (
                  <Button color='error' variant='outlined' onClick={() => setDocumentGallery([])}>
                    Remove Uploaded Documents
                  </Button>
                )}
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, m: 3 }}>
                <Stack spacing={3}>
                  {!isAdd && (
                    <>
                      <Typography variant='subtitle1'>Status</Typography>
                      <Chip
                        label={(STATUS_META[selectedDataObj?.status] || { label: selectedDataObj?.status || 'New' }).label}
                        color={(STATUS_META[selectedDataObj?.status] || { color: 'default' }).color}
                      />
                    </>
                  )}
                  {canEditSelectedBill && (
                    <LoadingButton type='submit' variant='contained' size='large' sx={{ width: '100%', ...locationActionSx }} onClick={handleSubmit}>
                      {isAdd ? 'Create & Send to Finance' : 'Save & Resend to Finance'}
                    </LoadingButton>
                  )}
                  <Button color='info' variant='outlined' size='large' style={{ width: '100%' }} onClick={() => manageModel()}>
                    Close
                  </Button>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </SwipeableDrawer>

        {isViewerOpen && <ImageViewer src={imageGallery} currentIndex={currentImage} disableScroll={false} closeOnClickOutside={true} onClose={closeImageViewer} />}
        <PdfViewerDrawer open={isPdfDrawerOpen} onClose={() => setIsPdfDrawerOpen(false)} invoice={pdfRow} workflowLog={workflowLog} />
      </Container>
    </Page>
  );
}
